import { Component, computed, effect, inject, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import {
  companyBrandFallbackColor,
  companyInitials,
} from '@core/auth/company-membership-label.util';
import { LocationContextService } from '@core/tenant/location-context.service';
import { OperatedTenantSafetyService } from '@core/tenant/operated-tenant-safety.service';
import { TenantContextService } from '@core/tenant/tenant-context.service';
import { TenantCompanySwitchService } from '@core/tenant/tenant-company-switch.service';
import { openWorkspacePickerAndApply } from '@core/shell/workspace-picker-dialog/open-workspace-picker.util';

@Component({
  selector: 'app-company-switcher',
  imports: [MaterialModule, TablerIconsModule, TranslateModule],
  templateUrl: './company-switcher.component.html',
  styleUrl: './company-switcher.component.scss',
  host: { class: 'company-switcher-host shrink-0' },
})
export class CompanySwitcherComponent {
  readonly switchService = inject(TenantCompanySwitchService);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly tenantContext = inject(TenantContextService);
  private readonly locationContext = inject(LocationContextService);
  private readonly operatedTenantSafety = inject(OperatedTenantSafetyService);
  private readonly translate = inject(TranslateService);

  readonly isOperatedTenant = this.operatedTenantSafety.isOperatingForeignTenant;
  private readonly logoBroken = signal(false);

  readonly activeLogoUrl = computed(() => {
    const url = this.switchService.activeCompany()?.logoUrl?.trim() || '';
    return this.logoBroken() ? '' : url;
  });
  readonly activeInitials = computed(() =>
    companyInitials(this.switchService.activeCompany()?.name ?? '')
  );
  readonly activeBrandColor = computed(() => {
    const company = this.switchService.activeCompany();
    if (!company) {
      return '#1f2937';
    }
    return companyBrandFallbackColor(company.id, company.primaryColor);
  });

  readonly operatedHint = computed(() => {
    if (!this.isOperatedTenant()) {
      return null;
    }
    const name = this.operatedTenantSafety.operatedTenantName() ?? '—';
    return this.translate.instant('platform.operatedTenant.banner', { name });
  });

  readonly pillAriaLabel = computed(() => {
    const base = this.translate.instant('header.companySwitcher.menuLabel');
    const hint = this.operatedHint();
    return hint ? `${base}. ${hint}` : base;
  });

  constructor() {
    effect(() => {
      void this.switchService.activeCompany()?.logoUrl;
      this.logoBroken.set(false);
    });
  }

  onLogoError(): void {
    this.logoBroken.set(true);
  }

  openPicker(): void {
    openWorkspacePickerAndApply(this.dialog, {
      companies: this.switchService.accessibleCompanies(),
      activeCompanyId: this.switchService.activeCompanyId(),
      allowCreateBusiness: true,
      switchService: this.switchService,
      router: this.router,
      tenantContext: this.tenantContext,
      locationContext: this.locationContext,
    });
  }
}
